import { z } from 'zod';
import { esquemaCorreo } from './auth';

/**
 * M2 — Empresas cliente.
 *
 * Es la base del aislamiento: toda tabla de negocio lleva `empresa_id` (D-006).
 * Acá solo viven las empresas **cliente**; Copathy también es una fila de
 * `empresas`, pero con `tipo = 'proveedor'`, y no se administra por este módulo
 * — un índice único impide que exista una segunda.
 */

export const esquemaNombreEmpresa = z
  .string()
  .trim()
  .min(2, 'El nombre es demasiado corto.')
  .max(160, 'El nombre es demasiado largo.');

/**
 * Identificación fiscal.
 *
 * ⚠️ Sin formato de RIF venezolano a propósito: Copathy tiene clientes en varios
 * países y cada uno numera distinto. Se normaliza a mayúsculas y sin espacios en
 * los bordes, que es exactamente lo que indexa `ux_empresas_rif` —si acá se
 * normalizara de otra forma, el índice único dejaría pasar duplicados.
 */
export const esquemaRif = z
  .string()
  .trim()
  .min(3, 'La identificación fiscal es demasiado corta.')
  .max(40, 'La identificación fiscal es demasiado larga.')
  .transform((v) => v.toUpperCase());

const opcionalTexto = (max: number) => z.string().trim().max(max).optional();

/*
  ── Sobre el «mínimo de respuestas para entregar un agregado» ──

  La columna `empresas.minimo_agregado` existe desde la migración 001 y sigue ahí,
  con su valor por omisión, pero **NO forma parte del contrato ni de la interfaz**.

  Era una propuesta técnica nuestra —el piso por debajo del cual no se le entrega un
  resultado agregado a la empresa, para que un promedio de tres personas no las
  identifique—, no un requerimiento de Copathy. `00-requerimientos.md` §12 ya la
  tenía marcada como «⚠️ CONFIRMAR». Copathy no la ha pedido, así que se omite hasta
  que la exprese (D-033).

  ⚠️ Lo que NO se está omitiendo es D-009: la empresa cliente sigue sin poder ver
  respuestas individuales identificadas. Eso lo sostienen el permiso
  `respuestas.ver_individuales`, la lista `PERMISOS_PROHIBIDOS_PARA_EMPRESA` y un
  trigger en el motor. Lo que queda pendiente es solo el piso numérico.
*/

/**
 * País del cliente: **ISO 3166-1 alfa-2 en minúscula** (`ve`, `co`, `us`).
 *
 * No se guarda el nombre —cambia de idioma— ni el prefijo telefónico: `+1` es de
 * Estados Unidos y de media docena de países del Caribe, así que derivar el país
 * del prefijo da respuestas equivocadas.
 */
export const esquemaPais = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z]{2}$/, 'Elige un país de la lista.');

/**
 * Cuántos trabajadores tiene la empresa. Es distinto de cuántos están registrados
 * en la plataforma, y **no limita nada**: Copathy vende programas, no usuarios
 * (D-034).
 */
export const esquemaEmpleados = z
  .number()
  .int('Tiene que ser un número entero.')
  .min(0, 'No puede ser negativo.')
  .max(10_000_000, 'Ese número no parece real.');

export const esquemaCrearEmpresa = z.object({
  nombre: esquemaNombreEmpresa,
  rif: esquemaRif.optional(),
  pais: esquemaPais.optional(),
  empleados: esquemaEmpleados.optional(),
  sectorId: z.number().int().positive().optional(),
  contactoNombre: opcionalTexto(120),
  contactoCorreo: esquemaCorreo.optional(),
  contactoTelefono: opcionalTexto(40),
});
export type EntradaCrearEmpresa = z.infer<typeof esquemaCrearEmpresa>;

/**
 * Edición parcial. Todo opcional, pero **no se acepta un cuerpo vacío**: un
 * PATCH sin campos escribiría una fila de auditoría por un cambio que no ocurrió.
 */
export const esquemaEditarEmpresa = z
  .object({
    nombre: esquemaNombreEmpresa.optional(),
    // `null` borra el dato; ausente lo deja como está. Son cosas distintas y por
    // eso no se pueden colapsar en `undefined`.
    rif: esquemaRif.nullable().optional(),
    pais: esquemaPais.nullable().optional(),
    empleados: esquemaEmpleados.nullable().optional(),
    sectorId: z.number().int().positive().nullable().optional(),
    contactoNombre: opcionalTexto(120).nullable(),
    contactoCorreo: esquemaCorreo.nullable().optional(),
    contactoTelefono: opcionalTexto(40).nullable(),
    activo: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarEmpresa = z.infer<typeof esquemaEditarEmpresa>;

/**
 * Rotar el código de registro (D-023).
 *
 * El código lo genera el servidor, nunca el cliente: si lo eligiera Copathy,
 * terminaría siendo el nombre de la empresa y sería adivinable. `diasVigencia`
 * ausente o nulo significa que no vence.
 */
export const esquemaRotarCodigo = z.object({
  diasVigencia: z.number().int().min(1).max(365).nullable().optional(),
});
export type EntradaRotarCodigo = z.infer<typeof esquemaRotarCodigo>;

/**
 * Lista separada por comas, como llega de la URL: `?pais=ve,co`.
 *
 * Se acepta CSV y no un arreglo repetido (`?pais=ve&pais=co`) porque es lo que el
 * buscador arma y lo que se lee de un vistazo en la barra de direcciones. Vacío se
 * vuelve `undefined`: un filtro sin valores no es «no traigas nada», es «no
 * filtres por esto».
 */
const listaCsv = (max: number) =>
  z
    .string()
    .optional()
    .transform((v) => {
      const partes = (v ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, max);
      return partes.length ? partes : undefined;
    });

export const esquemaFiltroEmpresas = z.object({
  texto: z.string().trim().max(160).optional(),
  activo: z
    .enum(['si', 'no', 'todas'])
    .default('si')
    .transform((v) => (v === 'todas' ? undefined : v === 'si')),
  /** ISO alfa-2, varios. El tope evita una query de mil marcadores. */
  pais: listaCsv(60),
  /** Ids del catálogo de sectores. */
  sector: listaCsv(60).transform((v) => v?.map(Number).filter(Number.isInteger)),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});
export type FiltroEmpresas = z.infer<typeof esquemaFiltroEmpresas>;

export const esquemaEmpresa = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  rif: z.string().nullable(),
  pais: z.string().nullable(),
  empleados: z.number().int().nullable(),
  sectorId: z.number().int().nullable(),
  /** Nombre del sector, resuelto desde el catálogo. Es lo que se pinta. */
  sector: z.string().nullable(),
  /** `true` si tiene logotipo cargado. La imagen se pide aparte, autenticada. */
  tieneLogo: z.boolean(),
  contactoNombre: z.string().nullable(),
  contactoCorreo: z.string().nullable(),
  contactoTelefono: z.string().nullable(),
  activo: z.boolean(),
  /**
   * ⚠️ El código va en la ficha, no en el listado. No es un secreto fuerte
   * —termina en un grupo de WhatsApp (D-023)— pero tampoco hace falta pintarlo
   * veinte veces en una pantalla que alguien puede tener abierta en una reunión.
   */
  codigoRegistro: z.string().nullable(),
  codigoRegistroExpiraEn: z.string().nullable(),
  trabajadores: z.number().int(),
  solicitudesPendientes: z.number().int(),
  creadoEn: z.string(),
  actualizadoEn: z.string(),
});
export type Empresa = z.infer<typeof esquemaEmpresa>;

/** Lo que va en el listado: sin el código de registro. */
export const esquemaEmpresaListada = esquemaEmpresa.omit({
  codigoRegistro: true,
  codigoRegistroExpiraEn: true,
});
export type EmpresaListada = z.infer<typeof esquemaEmpresaListada>;

/**
 * Los usuarios de un cliente, para su vista de detalle.
 *
 * ⚠️ Acá NO entra nada de lo que la persona respondió. Es la ficha administrativa
 * —quién es, en qué área está, si su cuenta sigue activa—, no su contenido. La
 * frontera 3 (D-009) no se cruza ni siquiera para Copathy por esta puerta: para
 * ver respuestas está el permiso `respuestas.ver_individuales`, que es otro.
 */
export const esquemaTrabajadorDeEmpresa = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
  correo: z.string(),
  area: z.string().nullable(),
  cargo: z.string().nullable(),
  activo: z.boolean(),
  /** Estado de la CUENTA. `null` si el trabajador todavía no tiene usuario. */
  estadoUsuario: z.enum(['sin_activar', 'activo', 'desactivado']).nullable(),
  creadoEn: z.string(),
});
export type TrabajadorDeEmpresa = z.infer<typeof esquemaTrabajadorDeEmpresa>;

export const esquemaFiltroTrabajadores = z.object({
  activo: z
    .enum(['si', 'no', 'todas'])
    .default('si')
    .transform((v) => (v === 'todas' ? undefined : v === 'si')),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});
export type FiltroTrabajadores = z.infer<typeof esquemaFiltroTrabajadores>;

/**
 * Catálogo de sectores.
 *
 * Es **global**, no por cliente: «Manufactura» es la misma para todos. Vive en la
 * tabla `catalogos` colgando de la fila de Copathy, que ya es la dueña de lo que
 * es de la casa (migración 007).
 */
export const esquemaSector = z.object({
  id: z.number().int().positive(),
  nombre: z.string(),
});
export type Sector = z.infer<typeof esquemaSector>;

/**
 * Alta de un sector desde el mismo campo donde se busca.
 *
 * ⚠️ El nombre se normaliza **igual que el índice único** `ux_catalogos_nombre`
 * —sin espacios en los bordes, sin distinguir mayúsculas—. Si acá se normalizara
 * distinto, el índice dejaría pasar duplicados y el catálogo terminaría con
 * «Manufactura», «manufactura» y «Manufactura » como tres sectores distintos.
 */
export const esquemaCrearSector = z.object({
  nombre: z
    .string()
    .trim()
    .min(2, 'El nombre del sector es demasiado corto.')
    .max(120, 'El nombre del sector es demasiado largo.'),
});
export type EntradaCrearSector = z.infer<typeof esquemaCrearSector>;

/**
 * Límites del logotipo. Viven en `shared/` porque los tiene que conocer el
 * navegador —para avisar ANTES de subir 8 MB por una conexión lenta— y el
 * servidor, que es el que de verdad decide.
 */
export const LOGO_TIPOS = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
/** Lado máximo tras procesar. Es un logotipo en una tarjeta, no una fotografía. */
export const LOGO_LADO_MAX = 512;
